// =============================================================================
//  Moteur de scan - version mock.
//
//  Simule l'execution du catalogue de controles sur un tenant en generant des
//  reponses Microsoft Graph plausibles. Le verdict (pass/fail/warning) est
//  derive de facon deterministe a partir de (tenantId, controlId) pour que les
//  scans soient reproductibles et coherents entre les rapports.
//
//  Quand le backend reel sera branche, il suffira de remplacer runMockScan()
//  par un appel a chaque checker Graph (un fichier par controle, conformement
//  a la propriete graphEndpoints du catalogue).
// =============================================================================

import { CATALOG_VERSION, controls } from './controls';
import {
  CheckEvidence,
  CheckStatus,
  ControlResult,
  Scan,
  ScoreBreakdown,
  Tenant,
  Family,
} from './types';
import { severityWeight } from './controls';

// ----------------------------------------------------------------------------
// Hash deterministe : on veut que (tenantId, controlId) donne toujours le meme
// verdict, sinon le score saute entre les pages et c'est incomprehensible pour
// l'utilisateur.
// ----------------------------------------------------------------------------
function hashString(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/** Renvoie un entier 0..n-1 deterministe a partir de la cle. */
function deterministicInt(key: string, n: number): number {
  return hashString(key) % n;
}

// ----------------------------------------------------------------------------
// Generateurs de preuve par controle - retournent un statut + une preuve brute
// "plausible" en simulant la forme reelle des reponses Graph.
//
// On parametre la qualite globale du tenant via tenantSeed pour que :
//   - Fabrikam (E5, bon score) ait majoritairement des pass
//   - Contoso (Pro, moyen)     ait un mix realiste
//   - Litware (Starter, faible) ait beaucoup de fail
// ----------------------------------------------------------------------------

/**
 * Profil de tenant - influence la distribution pass/fail/warning.
 * 'good' = ~80% pass, 'mixed' = ~50%, 'weak' = ~25%.
 */
type TenantProfile = 'good' | 'mixed' | 'weak';

function profileOfTenant(tenant: Tenant): TenantProfile {
  if (tenant.subscriptionPlan === 'enterprise') return 'good';
  if (tenant.subscriptionPlan === 'pro')        return 'mixed';
  return 'weak';
}

/**
 * Decide d'un statut deterministe pour un (tenant, control) couple.
 * Les controles "critical" sont sur-representes en pass pour les profils good.
 */
function decideStatus(tenantId: string, controlId: string, profile: TenantProfile): CheckStatus {
  const r = deterministicInt(`${tenantId}::${controlId}`, 100);

  if (profile === 'good') {
    if (r < 80) return 'pass';
    if (r < 90) return 'warning';
    if (r < 97) return 'fail';
    return 'manual';
  }
  if (profile === 'mixed') {
    if (r < 50) return 'pass';
    if (r < 70) return 'warning';
    if (r < 95) return 'fail';
    return 'manual';
  }
  // weak
  if (r < 25) return 'pass';
  if (r < 45) return 'warning';
  if (r < 95) return 'fail';
  return 'manual';
}

// ----------------------------------------------------------------------------
// Builders de preuve par controle - retournent une CheckEvidence avec une raw
// realiste qu'on pourra exposer dans le rapport JSON.
// ----------------------------------------------------------------------------

interface EvidenceFactoryInput {
  controlId: string;
  status: CheckStatus;
  tenantId: string;
  tenant: Tenant;
  now: string;
}

function makeEvidences(input: EvidenceFactoryInput): CheckEvidence[] {
  const { controlId, status, tenant, now } = input;

  switch (controlId) {
    case 'ID-001': {
      const total    = Math.max(20, tenant.size);
      const ratio    = status === 'pass' ? 0.97 : status === 'warning' ? 0.88 : 0.71;
      const enrolled = Math.round(total * ratio);
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /reports/authenticationMethods/userRegistrationDetails',
        summary:    `${enrolled} comptes sur ${total} disposent d'une methode MFA enregistree (${Math.round(ratio * 100)} %).`,
        raw: {
          totalUserCount:               total,
          userRegisteredMfa:            enrolled,
          userRegisteredFido2:          Math.round(enrolled * 0.18),
          userRegisteredAuthenticator:  Math.round(enrolled * 0.78),
          measurementDateTime:          now,
        },
      }];
    }

    case 'ID-002': {
      const policiesEnabled =
        status === 'pass' ? 1 :
        status === 'warning' ? 1 : 0;
      const policies = policiesEnabled ? [{
        id:           'b1d2c3a4-' + tenant.m365TenantId.slice(0, 12),
        displayName:  'Require MFA for all users',
        state:        status === 'pass' ? 'enabled' : 'enabledForReportingButNotEnforced',
        conditions:   { users: { includeUsers: ['All'] }, applications: { includeApplications: ['All'] } },
        grantControls:{ operator: 'OR', builtInControls: ['mfa'] },
      }] : [];
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /identity/conditionalAccess/policies',
        summary:    policiesEnabled
          ? `Politique CA active : ${policies[0].displayName} (${policies[0].state}).`
          : 'Aucune CA Policy n\'impose MFA. Security Defaults egalement desactives.',
        raw: { '@odata.count': policies.length, value: policies },
      }];
    }

    case 'ID-003': {
      const gaCount = status === 'pass' ? 3 : status === 'warning' ? 6 : 12;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /directoryRoles + /roleAssignments',
        summary:    `${gaCount} comptes Global Administrator detectes.`,
        raw: {
          roleId:        '62e90394-69f5-4237-9190-012177145e10',
          roleName:      'Global Administrator',
          memberCount:   gaCount,
          sample:        Array.from({ length: Math.min(gaCount, 3) }, (_, i) => ({
            userPrincipalName: `admin${i + 1}@${tenant.domain}`,
          })),
        },
      }];
    }

    case 'ID-004': {
      const pct = status === 'pass' ? 92 : status === 'warning' ? 65 : 18;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /roleManagement/directory/roleEligibilitySchedules',
        summary:    `${pct} % des assignments privilegies sont en mode eligible via PIM.`,
        raw: { eligiblePercent: pct, scheduled: 24, permanent: Math.round(24 * (1 - pct / 100)) },
      }];
    }

    case 'ID-005': {
      const blocked = status === 'pass';
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /identity/conditionalAccess/policies',
        summary:    blocked
          ? 'CA Policy "Block legacy authentication" active.'
          : 'Aucune CA Policy ne bloque les protocoles legacy (Basic Auth, IMAP, POP, SMTP AUTH).',
        raw: {
          policy: blocked ? {
            id:          'leg-' + tenant.m365TenantId.slice(0, 8),
            displayName: 'Block legacy authentication',
            state:       'enabled',
            conditions:  { clientAppTypes: ['exchangeActiveSync', 'other'] },
            grantControls:{ operator: 'OR', builtInControls: ['block'] },
          } : null,
        },
      }];
    }

    case 'ID-006': {
      const enabled = status !== 'fail';
      const methods = enabled ? 2 : 1;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /policies/authorizationPolicy',
        summary:    enabled
          ? `SSPR active, ${methods} methodes de verification requises.`
          : 'SSPR desactive pour les utilisateurs standard.',
        raw: { allowedToUseSSPR: enabled, requiredMethods: methods },
      }];
    }

    case 'ID-007': {
      const count = status === 'pass' ? 0 : status === 'warning' ? 3 : 17;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /users (filter passwordPolicies)',
        summary:    `${count} compte(s) avec mot de passe n'expirant jamais.`,
        raw: { matchingUsers: count, threshold: 0 },
      }];
    }

    case 'ID-008': {
      const reviewsActive = status === 'pass' ? 2 : status === 'warning' ? 1 : 0;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /identityGovernance/accessReviews/definitions',
        summary:    reviewsActive > 0
          ? `${reviewsActive} access review(s) actives ciblant les invites.`
          : 'Aucune access review configuree pour les invites.',
        raw: { activeReviews: reviewsActive, guestUsersTotal: Math.round(tenant.size * 0.12) },
      }];
    }

    case 'EM-001': {
      const presetActive = status === 'pass' ? 'Strict' : status === 'warning' ? 'Standard' : 'None';
      return [{
        capturedAt: now,
        source:     'Defender for Office 365 - Anti-phishing policies',
        summary:    presetActive === 'None'
          ? 'Aucune politique anti-phishing active.'
          : `Politique anti-phishing active : preset ${presetActive}.`,
        raw: {
          preset:         presetActive,
          impersonationProtectionEnabled: presetActive !== 'None',
          mailboxIntelligence:            presetActive === 'Strict',
        },
      }];
    }

    case 'EM-002': {
      const enabled = status === 'pass';
      return [{
        capturedAt: now,
        source:     'Defender for Office 365 - Safe Attachments policies',
        summary:    enabled
          ? 'Safe Attachments active sur Exchange + SharePoint + Teams (action Block).'
          : 'Safe Attachments inactive ou en mode Off.',
        raw: { action: enabled ? 'Block' : 'Off', workloads: enabled ? ['Exchange', 'SharePoint', 'Teams'] : [] },
      }];
    }

    case 'EM-003': {
      const enabled = status !== 'fail';
      return [{
        capturedAt: now,
        source:     'Defender for Office 365 - Safe Links policies',
        summary:    enabled
          ? 'Safe Links active avec time-of-click protection.'
          : 'Safe Links inactive.',
        raw: { trackClicks: enabled, scanUrls: enabled, deliverMessageAfterScan: enabled },
      }];
    }

    case 'EM-004': {
      const domains = [tenant.domain];
      const enabled = status === 'pass';
      return [{
        capturedAt: now,
        source:     'Exchange Online - Get-DkimSigningConfig',
        summary:    enabled
          ? `DKIM active sur ${domains.length} domaine(s).`
          : 'DKIM non publie ou desactive sur le domaine principal.',
        raw: domains.map((d) => ({ domain: d, enabled, selector1CNAMEPublished: enabled, selector2CNAMEPublished: enabled })),
      }];
    }

    case 'EM-005': {
      const policy = status === 'pass' ? 'reject' : status === 'warning' ? 'quarantine' : 'none';
      return [{
        capturedAt: now,
        source:     `DNS TXT _dmarc.${tenant.domain}`,
        summary:    `Politique DMARC publiee : p=${policy}.`,
        raw: { record: `v=DMARC1; p=${policy}; rua=mailto:dmarc@${tenant.domain}; pct=100` },
      }];
    }

    case 'EM-006': {
      const all = status === 'pass' ? '-all' : status === 'warning' ? '~all' : '?all';
      return [{
        capturedAt: now,
        source:     `DNS TXT ${tenant.domain}`,
        summary:    `SPF se termine par "${all}".`,
        raw: { record: `v=spf1 include:spf.protection.outlook.com ${all}` },
      }];
    }

    case 'DP-001': {
      const count = status === 'pass' ? 3 : status === 'warning' ? 1 : 0;
      return [{
        capturedAt: now,
        source:     'Microsoft Purview - DLP compliance policies',
        summary:    count > 0
          ? `${count} politique(s) DLP actives couvrant PII / cartes de credit.`
          : 'Aucune politique DLP active.',
        raw: {
          policies: Array.from({ length: count }, (_, i) => ({
            name:      ['EU PII protection', 'PCI DSS - Credit Cards', 'Internal Confidential'][i],
            mode:      'Enforce',
            workloads: ['Exchange', 'SharePoint', 'OneDrive', 'Teams'],
          })),
        },
      }];
    }

    case 'DP-002': {
      const labels = status === 'pass' ? 4 : status === 'warning' ? 2 : 0;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /security/informationProtection/labelPolicySettings',
        summary:    `${labels} sensitivity label(s) publies aux utilisateurs.`,
        raw: {
          labels: ['Public', 'Internal', 'Confidential', 'Highly Confidential'].slice(0, labels),
          encryptionEnabledOnTopLabel: labels >= 4,
        },
      }];
    }

    case 'DP-003': {
      const level = status === 'pass' ? 'ExistingExternalUserSharingOnly'
                  : status === 'warning' ? 'ExternalUserSharingOnly'
                  : 'ExternalUserAndGuestSharing';
      return [{
        capturedAt: now,
        source:     'SharePoint Admin - Get-SPOTenant',
        summary:    `Niveau de partage externe SharePoint : ${level}.`,
        raw: { SharingCapability: level },
      }];
    }

    case 'DP-004': {
      const aligned = status !== 'fail';
      return [{
        capturedAt: now,
        source:     'SharePoint Admin - Get-SPOTenant (OneDriveSharingCapability)',
        summary:    aligned
          ? 'OneDrive sharing capability aligne sur SharePoint.'
          : 'OneDrive est plus permissif que SharePoint - desalignement.',
        raw: {
          SharePointSharingCapability: 'ExistingExternalUserSharingOnly',
          OneDriveSharingCapability:   aligned ? 'ExistingExternalUserSharingOnly' : 'ExternalUserAndGuestSharing',
        },
      }];
    }

    case 'DP-005': {
      const policies = status === 'pass' ? 3 : status === 'warning' ? 1 : 0;
      return [{
        capturedAt: now,
        source:     'Microsoft Purview - Get-RetentionCompliancePolicy',
        summary:    policies > 0
          ? `${policies} politique(s) de retention active(s).`
          : 'Aucune politique de retention configuree.',
        raw: {
          policies: Array.from({ length: policies }, (_, i) => ({
            name:           ['Default 7 years', 'HR retention 10 years', 'Finance retention 10 years'][i],
            retentionYears: [7, 10, 10][i],
            workloads:      ['Exchange', 'SharePoint', 'OneDrive'],
          })),
        },
      }];
    }

    case 'DP-006': {
      const compliantPct = status === 'pass' ? 98 : status === 'warning' ? 84 : 51;
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /deviceManagement/managedDevices',
        summary:    `${compliantPct} % des postes Windows ont BitLocker active.`,
        raw: { totalDevices: tenant.size, bitlockerCompliantPercent: compliantPct },
      }];
    }

    case 'LG-001': {
      const enabled = status === 'pass';
      return [{
        capturedAt: now,
        source:     'Exchange Online - Get-AdminAuditLogConfig',
        summary:    enabled
          ? 'Unified Audit Log active au niveau du tenant.'
          : 'Unified Audit Log desactive - aucune trace conservee.',
        raw: { UnifiedAuditLogIngestionEnabled: enabled },
      }];
    }

    case 'LG-002': {
      const days = status === 'pass' ? 365 : status === 'warning' ? 180 : 90;
      return [{
        capturedAt: now,
        source:     'Purview Audit - Get-RetentionPolicy',
        summary:    `Retention des logs : ${days} jours.`,
        raw: { retentionDays: days, requiredByNIS2: 365 },
      }];
    }

    case 'LG-003': {
      const configured = status === 'pass';
      return [{
        capturedAt: now,
        source:     'Microsoft Graph GET /identityProtection/riskyUsers',
        summary:    configured
          ? 'User risk policy active (High risk = require password change).'
          : 'Aucune alert policy sur les sign-ins risques.',
        raw: { riskyUsersDetected: 7, alertPolicyConfigured: configured },
      }];
    }

    case 'LG-004': {
      const connected = status !== 'fail';
      return [{
        capturedAt: now,
        source:     'Defender XDR - Cloud Apps connectors',
        summary:    connected
          ? 'Defender for Cloud Apps connecte (Office 365 + Azure).'
          : 'Defender for Cloud Apps non connecte.',
        raw: { connectors: connected ? ['Office 365', 'Microsoft Azure'] : [] },
      }];
    }

    case 'LG-005': {
      return [{
        capturedAt: now,
        source:     'Verification manuelle - politique IR documentee',
        summary:    'Controle manuel : verifier la presence d\'un playbook IR a jour.',
        raw: { manual: true, suggestedTemplate: 'NIST SP 800-61 r2' },
      }];
    }

    default:
      return [{
        capturedAt: now,
        source:     'Mock',
        summary:    'Reponse simulee generique.',
        raw:        {},
      }];
  }
}

/** Message court resumant le verdict (sert dans les listes / dashboards). */
function decisionMessage(controlId: string, status: CheckStatus, evidence: CheckEvidence): string {
  if (status === 'pass')           return evidence.summary;
  if (status === 'warning')        return `A ameliorer : ${evidence.summary}`;
  if (status === 'fail')           return `Non conforme : ${evidence.summary}`;
  if (status === 'manual')         return 'Verification manuelle requise (preuve documentaire).';
  if (status === 'not_applicable') return 'Non applicable au plan de licences du tenant.';
  return `Erreur lors de l'evaluation du controle ${controlId}.`;
}

/** Score numerique d'un statut (0..1). */
function statusScore(status: CheckStatus): number {
  switch (status) {
    case 'pass':           return 1;
    case 'warning':        return 0.5;
    case 'manual':         return 0.5;  // suppose preuve fournie - depondere apres revue
    case 'fail':           return 0;
    case 'not_applicable': return 1;     // ne penalise pas
    case 'error':          return 0;
  }
}

// ----------------------------------------------------------------------------
// API publique
// ----------------------------------------------------------------------------

export interface RunScanOptions {
  triggeredBy?: string;
  /** Filtre sur une famille ou un sous-ensemble de controles. */
  onlyControlIds?: string[];
  /** Date a utiliser comme "now" - utile pour les tests / persistance. */
  now?: Date;
}

/** Execute le catalogue (ou un sous-ensemble) sur le tenant et retourne un Scan. */
export function runMockScan(tenant: Tenant, options: RunScanOptions = {}): Scan {
  const now       = options.now ?? new Date();
  const startedAt = now.toISOString();
  const profile   = profileOfTenant(tenant);

  const selectedControls = options.onlyControlIds
    ? controls.filter((c) => options.onlyControlIds!.includes(c.id))
    : controls;

  const results: ControlResult[] = selectedControls.map((control) => {
    const status     = control.manual ? 'manual' : decideStatus(tenant.id, control.id, profile);
    const evidences  = makeEvidences({
      controlId: control.id,
      status,
      tenantId:  tenant.id,
      tenant,
      now:       startedAt,
    });
    const message    = decisionMessage(control.id, status, evidences[0]);
    return {
      controlId:   control.id,
      status,
      score:       statusScore(status),
      message,
      evidences,
      evaluatedAt: startedAt,
    };
  });

  // Simule une duree de scan realiste (50-150ms par controle).
  const finishedAt = new Date(now.getTime() + results.length * 90).toISOString();

  return {
    id:         `scan-${tenant.id}-${now.getTime()}`,
    tenantId:   tenant.id,
    startedAt,
    finishedAt,
    results,
    meta: {
      triggeredBy:    options.triggeredBy ?? 'manual',
      scanType:       options.onlyControlIds ? 'partial' : 'full',
      catalogVersion: CATALOG_VERSION,
    },
  };
}

// ----------------------------------------------------------------------------
// Agregation / score
// ----------------------------------------------------------------------------

/**
 * Applique les decisions manuelles persistees du tenant sur un scan brut.
 * Retourne un nouveau Scan avec les status/score overlayes.
 * Pure function - n'altere pas l'entree.
 *
 *  - decision 'conforme' -> status='pass' + score=1.0 (compte comme un pass auto)
 *  - decision 'fail'     -> status='fail' + score=0
 *  - decision 'skip'     -> status='not_applicable' + score=0 (exclu du denom)
 *  - decision 'pending'  -> pas de changement (reste manual)
 *  - pas de decision     -> reste tel quel
 */
export function applyManualDecisions(
  scan: Scan,
  decisions: Record<string, { decision: 'conforme' | 'fail' | 'skip' | 'pending' }>,
): Scan {
  if (!decisions || Object.keys(decisions).length === 0) return scan;
  return {
    ...scan,
    results: scan.results.map((r) => {
      const d = decisions[r.controlId];
      if (!d || d.decision === 'pending') return r;
      if (d.decision === 'conforme')      return { ...r, status: 'pass',           score: 1.0 };
      if (d.decision === 'fail')          return { ...r, status: 'fail',           score: 0.0 };
      if (d.decision === 'skip')          return { ...r, status: 'not_applicable', score: 0.0 };
      return r;
    }),
  };
}

/**
 * Calcule le score global et par famille pour un scan.
 *
 * REGLE DENOMINATEUR (cf. worker RunScan/run.ps1) :
 *   - On EXCLUT 'manual' et 'not_applicable' du denominateur. Ces statuts ne
 *     sont pas auditables techniquement et leur poids ne doit pas plomber la
 *     posture mesuree. Pour qu'un manuel "compte", l'auditeur doit poser une
 *     decision via la page /manual-checks qui le convertit en pass/fail.
 *   - 'error' EST inclus dans le denominateur (avec score 0). Une erreur
 *     technique persistante doit faire baisser le score - elle indique un
 *     probleme de permissions/connexion a remediar.
 */
export function computeScoreBreakdown(scan: Scan): ScoreBreakdown {
  // Sous-totaux separes auto / manuel pour exposer les 2 sous-scores au front.
  // - auto   : controles dont le catalogue indique manual:false (audites par scan)
  // - manuel : controles dont le catalogue indique manual:true (necessitent decision humaine)
  let autoWeight   = 0; let autoScore   = 0;
  let manualWeight = 0; let manualScore = 0;

  const familyTotals: Record<Family, { weight: number; score: number }> = {
    identity: { weight: 0, score: 0 },
    email:    { weight: 0, score: 0 },
    data:     { weight: 0, score: 0 },
    logs:     { weight: 0, score: 0 },
    endpoint: { weight: 0, score: 0 },
    collab:   { weight: 0, score: 0 },
    apps:     { weight: 0, score: 0 },
  };

  const counts = { pass: 0, fail: 0, warning: 0, manual: 0, notApplicable: 0, error: 0, total: 0 };

  for (const r of scan.results) {
    const def = controls.find((c) => c.id === r.controlId);
    if (!def) continue;
    const w = severityWeight[def.severity];

    // Exclut 'manual' et 'not_applicable' du score (mais on les compte pour stats).
    const isScored = r.status !== 'manual' && r.status !== 'not_applicable';
    if (isScored) {
      familyTotals[def.family].weight += w;
      familyTotals[def.family].score  += w * r.score;

      // Decompose auto vs manuel selon le catalogue.
      // Un controle 'manual:true' qui a recu une decision (status overlayed vers pass/fail)
      // entre dans le manualScore. Un controle 'manual:false' (auto par nature) dans autoScore.
      if (def.manual) {
        manualWeight += w;
        manualScore  += w * r.score;
      } else {
        autoWeight += w;
        autoScore  += w * r.score;
      }
    }

    counts.total += 1;
    switch (r.status) {
      case 'pass':           counts.pass += 1; break;
      case 'fail':           counts.fail += 1; break;
      case 'warning':        counts.warning += 1; break;
      case 'manual':         counts.manual += 1; break;
      case 'not_applicable': counts.notApplicable += 1; break;
      case 'error':          counts.error += 1; break;
    }
  }

  // Combine les deux sous-poids pour le score global (moyenne ponderee unifiee).
  const totalWeight = autoWeight + manualWeight;
  const totalScore  = autoScore  + manualScore;

  const byFamily = {
    identity: familyTotals.identity.weight ? Math.round(100 * familyTotals.identity.score / familyTotals.identity.weight) : 0,
    email:    familyTotals.email.weight    ? Math.round(100 * familyTotals.email.score    / familyTotals.email.weight)    : 0,
    data:     familyTotals.data.weight     ? Math.round(100 * familyTotals.data.score     / familyTotals.data.weight)     : 0,
    logs:     familyTotals.logs.weight     ? Math.round(100 * familyTotals.logs.score     / familyTotals.logs.weight)     : 0,
    endpoint: familyTotals.endpoint.weight ? Math.round(100 * familyTotals.endpoint.score / familyTotals.endpoint.weight) : 0,
    collab:   familyTotals.collab.weight   ? Math.round(100 * familyTotals.collab.score   / familyTotals.collab.weight)   : 0,
    apps:     familyTotals.apps.weight     ? Math.round(100 * familyTotals.apps.score     / familyTotals.apps.weight)     : 0,
  };

  return {
    globalScore: totalWeight  ? Math.round(100 * totalScore  / totalWeight)  : 0,
    autoScore:   autoWeight   ? Math.round(100 * autoScore   / autoWeight)   : 0,
    manualScore: manualWeight ? Math.round(100 * manualScore / manualWeight) : 0,
    autoWeight,
    manualWeight,
    byFamily,
    counts,
  };
}
